<template>
  <div>
    <div class="row connections-page">
      <div class="col-lg-12">
        <KTPortlet v-bind:title="$t('MENU.CONNECTIONS')">
          <template v-slot:toolbar>
            <div class="connections-toolbar">
              <span class="live-indicator">
                <span class="live-indicator__dot"></span>
                Live data
              </span>
              <b-form-checkbox
                v-model="autoRefresh.enabled"
                switch
                class="mb-0 mr-4"
                :disabled="!selectedDomainName"
                @change="onAutoRefreshChange"
              >
                Auto-Refresh
              </b-form-checkbox>
              <b-button
                type="button"
                variant="primary"
                class="mr-4"
                :disabled="connectionsTable.loading || !selectedDomainName"
                @click="refreshConnections"
              >
                <v-icon small left color="white">refresh</v-icon>
                {{ $t("COMMON.REFRESH") }}
              </b-button>
            </div>
          </template>
          <template v-slot:body>
            <div class="row table-filter">
              <div class="col-sm-6">
                <b-form-group
                  :label="$t('COMMON.SEL_DOMAIN')"
                  label-for="select-domain"
                >
                  <model-list-select
                    :list="selectDomain.allDomains"
                    v-model="selectDomain.selectedDomain"
                    option-value="domain"
                    option-text="domain"
                    :placeholder="$t('COMMON.SEL_DOMAIN')"
                    @input="onChangeDomain"
                    id="select-domain"
                  >
                  </model-list-select>
                </b-form-group>
              </div>
              <div class="col-sm-6">
                <v-text-field
                  v-model="connectionsTable.search"
                  append-icon="search"
                  :label="$t('COMMON.SEARCH')"
                  class="cus-searchBox"
                  single-line
                  hide-details
                  @input="onSearchInput"
                ></v-text-field>
              </div>
            </div>

            <v-data-table
              :headers="headers"
              :items="filteredConnections"
              :items-per-page="connectionsTable.itemPerPage"
              :options.sync="connectionsTable.pagination"
              :expanded.sync="connectionsTable.expanded"
              :loading="connectionsTable.loading"
              :no-data-text="$t('COMMON.NO_DATA')"
              item-key="connection_key"
              class="elevation-1"
              ref="connectionsTable"
              show-expand
              :footer-props="{
                showFirstLastPage: true,
                'items-per-page-options': [5, 10, 15, 25, 50, -1],
              }"
              @update:items-per-page="changePerPage"
            >
              <template v-slot:item.uname="{ item }">
                {{ displayValue(item.uname) }}
              </template>

              <template v-slot:item.disabled="{ item }">
                <v-chip
                  small
                  :color="disabledColor(item.disabled)"
                  class="text-white"
                >
                  {{ disabledLabel(item.disabled) }}
                </v-chip>
              </template>

              <template v-slot:item.session_count="{ item }">
                <v-chip small color="primary" class="text-white">
                  {{ item.session_count }}
                </v-chip>
              </template>

              <template v-slot:item.last_observed="{ item }">
                {{ formatObserved(item.last_observed) }}
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="connections-session-cell">
                  <div class="connections-session-table">
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th class="text-left">#</th>
                          <th class="text-left">Observed</th>
                          <th class="text-left">State</th>
                          <th class="text-left">Command</th>
                          <th class="text-left">User</th>
                          <th class="text-left">IP</th>
                          <th class="text-left">Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!item.sessions.length">
                          <td colspan="7" class="text-center">
                            {{ $t("COMMON.NO_DATA") }}
                          </td>
                        </tr>
                        <tr
                          v-for="(session, sessionIndex) in item.sessions"
                          :key="sessionRowKey(item, sessionIndex)"
                        >
                          <td>{{ sessionIndex + 1 }}</td>
                          <td>{{ formatObserved(session.observed) }}</td>
                          <td>{{ formatState(session.state) }}</td>
                          <td>{{ displayValue(session.command) }}</td>
                          <td>{{ displayValue(session.user) }}</td>
                          <td>{{ displayValue(session.ip) }}</td>
                          <td>{{ formatLocation(session) }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </div>
                </td>
              </template>
            </v-data-table>
          </template>
        </KTPortlet>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/store/breadcrumbs.module";
import KTPortlet from "@/views/partials/content/Portlet.vue";
import { postRequest } from "@/api/request.js";

export default {
  name: "connections",
  components: {
    KTPortlet,
  },
  data() {
    return {
      connectionsTable: {
        users: [],
        expanded: [],
        search: "",
        itemPerPage: 10,
        pagination: {},
        loading: false,
      },
      selectDomain: {
        selectedDomain: {},
        allDomains: [],
      },
      autoRefresh: {
        enabled: false,
        timer: null,
        intervalMs: 30000,
      },
    };
  },
  computed: {
    headers: function () {
      return [
        { text: "", value: "data-table-expand", sortable: false },
        {
          text: this.$t("COMMON.USERNAME"),
          value: "username",
          sortable: true,
        },
        {
          text: this.$t("COMMON.FULL_NAME"),
          value: "uname",
          sortable: true,
        },
        {
          text: this.$t("COMMON.DISABLED"),
          value: "disabled",
          sortable: true,
        },
        {
          text: "Sessions",
          value: "session_count",
          sortable: true,
        },
        {
          text: "Last Seen",
          value: "last_observed",
          sortable: true,
        },
      ];
    },
    selectedDomainName() {
      return (
        this.selectDomain.selectedDomain &&
        this.selectDomain.selectedDomain.domain
      );
    },
    filteredConnections() {
      const search = (this.connectionsTable.search || "")
        .trim()
        .toLowerCase();

      if (!search) {
        return this.connectionsTable.users;
      }

      return this.connectionsTable.users.filter((user) => {
        return user.search_index.indexOf(search) !== -1;
      });
    },
  },
  created() {
    this.loadDomains();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: this.$t("MENU.USERS") + " -> " + this.$t("MENU.CONNECTIONS") },
    ]);
  },
  beforeDestroy() {
    this.stopAutoRefresh();
  },
  methods: {
    loadDomains() {
      postRequest({
        action: "getAllDomains",
        token: localStorage.id_token,
      }).then((res) => {
        if (!res) {
          return;
        }

        const regularDomains = this.normalizeDomainResponse(res);

        postRequest({
          action: "getAllAuditDomains",
          token: localStorage.id_token,
        }).then((auditRes) => {
          const auditDomains =
            auditRes && auditRes.returncode && Array.isArray(auditRes.returndata)
              ? auditRes.returndata
              : [];

          this.selectDomain.allDomains = auditDomains.concat(regularDomains);

          if (this.selectDomain.allDomains.length) {
            this.selectDomain.selectedDomain = this.selectDomain.allDomains[0];
            this.refreshConnections({ clear: true, resetPage: true });
          }
        });
      });
    },
    normalizeDomainResponse(res) {
      if (!res.returncode) {
        return res.returndata ? [{ domain: res.returndata }] : [];
      }

      if (!Array.isArray(res.returndata)) {
        return [];
      }

      return res.returndata.filter((domain) => {
        if (!domain || typeof domain !== "object") {
          return false;
        }

        if (domain.verification === undefined || domain.verification === null) {
          return true;
        }

        return String(domain.verification) === "0";
      });
    },
    refreshConnections(options = {}) {
      if (!this.selectedDomainName) {
        this.applyLiveUsersResponse({ returncode: 1, returndata: [] });
        this.stopAutoRefresh();
        return;
      }

      this.getLiveUsersForSelectedDomain(this.selectedDomainName, options);
    },
    getLiveUsersForSelectedDomain(domain, options = {}) {
      const requestId = this.nextConnectionsRequestId();
      const clearBeforeLoad = Boolean(options.clear);

      this.connectionsTable.loading = true;

      if (clearBeforeLoad) {
        this.connectionsTable.users = [];
        this.connectionsTable.expanded = [];
      }

      postRequest({
        action: "getLiveUsers",
        token: localStorage.id_token,
        domain: domain,
      })
        .then((res) => {
          this.applyLiveUsersResponse(res, requestId, options);
        })
        .finally(() => {
          if (requestId === this._connectionsRequestId) {
            this.connectionsTable.loading = false;
          }
        });
    },
    applyLiveUsersResponse(res, requestId, options = {}) {
      if (requestId && requestId !== this._connectionsRequestId) {
        return;
      }

      if (res && res.returncode) {
        const rows = Array.isArray(res.returndata) ? res.returndata : [];

        this.connectionsTable.users = rows.map((user, index) => {
          return this.normalizeLiveUser(user, index);
        });

        if (options.resetPage) {
          this.resetTablePage();
        }

        return;
      }

      if (options.clear) {
        this.connectionsTable.users = [];
        this.resetTablePage();
      }

      if (res && res.returndata) {
        this.makeToastVariant("danger", res.returndata);
      }
    },
    normalizeLiveUser(user, index) {
      const sessions = Array.isArray(user.sessions) ? user.sessions : [];
      const normalizedSessions = sessions.map((session) => {
        return { ...session };
      });
      const lastObserved = normalizedSessions.reduce((latest, session) => {
        const observed = Number(session.observed);
        return observed > latest ? observed : latest;
      }, 0);
      const normalizedUser = {
        ...user,
        connection_key: this.userKey(user, index),
        sessions: normalizedSessions,
        session_count: normalizedSessions.length,
        last_observed: lastObserved || "",
      };

      normalizedUser.search_index = this.connectionSearchIndex(normalizedUser);

      return normalizedUser;
    },
    connectionSearchIndex(user) {
      const parts = [
        user.username,
        user.domain,
        user.email,
        user.uname,
        this.disabledLabel(user.disabled),
        user.session_count,
        user.last_observed,
      ];

      user.sessions.forEach((session) => {
        parts.push(
          session.observed,
          this.formatObserved(session.observed),
          session.user,
          session.ip,
          session.state,
          this.formatState(session.state),
          session.command,
          session.location_cc,
          session.location_country
        );
      });

      return parts
        .filter((part) => part !== undefined && part !== null && part !== "")
        .join(" ")
        .toLowerCase();
    },
    userKey(user, index) {
      if (user.email) {
        return user.email;
      }

      if (user.username && user.domain) {
        return user.username + "@" + user.domain;
      }

      return "connection-" + index;
    },
    formatObserved(value) {
      const observed = Number(value);

      if (!observed) {
        return "-";
      }

      const date = new Date(observed * 1000);

      if (isNaN(date.getTime())) {
        return "-";
      }

      return date.toLocaleString();
    },
    formatLocation(session) {
      const country = session.location_country || "";
      const cc = session.location_cc || "";

      if (country && cc) {
        return country + " (" + cc + ")";
      }

      return country || cc || "-";
    },
    formatState(value) {
      if (value === undefined || value === null || value === "") {
        return "-";
      }

      return String(value).toUpperCase();
    },
    displayValue(value) {
      if (value === undefined || value === null || value === "") {
        return "-";
      }

      return value;
    },
    disabledLabel(value) {
      if (String(value) === "1") {
        return this.$t("COMMON.YES");
      }

      if (String(value) === "0") {
        return this.$t("COMMON.NO");
      }

      if (String(value) === "2") {
        return this.$t("USERS.DISABLE_SMTP_ONLY");
      }

      return this.displayValue(value);
    },
    disabledColor(value) {
      if (String(value) === "1") {
        return "red";
      }

      if (String(value) === "2") {
        return "orange";
      }

      return "green";
    },
    sessionRowKey(item, index) {
      return item.connection_key + "-session-" + index;
    },
    onChangeDomain(newValue) {
      this.connectionsTable.search = "";

      if (!newValue || !newValue.domain) {
        this.connectionsTable.users = [];
        this.connectionsTable.expanded = [];
        this.resetTablePage();
        this.stopAutoRefresh();
        return;
      }

      this.getLiveUsersForSelectedDomain(newValue.domain, {
        clear: true,
        resetPage: true,
      });

      if (this.autoRefresh.enabled) {
        this.startAutoRefresh();
      }
    },
    onAutoRefreshChange(enabled) {
      const isEnabled = Boolean(enabled);
      this.autoRefresh.enabled = isEnabled;

      if (isEnabled && this.selectedDomainName) {
        this.startAutoRefresh();
        return;
      }

      this.stopAutoRefresh();
    },
    startAutoRefresh() {
      this.clearAutoRefreshTimer();

      if (!this.autoRefresh.enabled || !this.selectedDomainName) {
        return;
      }

      this.autoRefresh.timer = setInterval(() => {
        if (!this.selectedDomainName) {
          this.stopAutoRefresh();
          return;
        }

        if (!this.connectionsTable.loading) {
          this.refreshConnections();
        }
      }, this.autoRefresh.intervalMs);
    },
    stopAutoRefresh() {
      this.clearAutoRefreshTimer();
      this.autoRefresh.enabled = false;
    },
    clearAutoRefreshTimer() {
      if (this.autoRefresh.timer) {
        clearInterval(this.autoRefresh.timer);
        this.autoRefresh.timer = null;
      }
    },
    onSearchInput() {
      this.resetTablePage();
    },
    changePerPage(arg) {
      this.connectionsTable.itemPerPage = arg;
    },
    resetTablePage() {
      this.connectionsTable.pagination = {
        ...this.connectionsTable.pagination,
        page: 1,
      };

      this.$nextTick(() => {
        if (this.$refs.connectionsTable) {
          this.$refs.connectionsTable.options.page = 1;
        }
      });
    },
    nextConnectionsRequestId() {
      this._connectionsRequestId = (this._connectionsRequestId || 0) + 1;
      return this._connectionsRequestId;
    },
    makeToastVariant(variant = null, msg) {
      let title = this.$t("COMMON.ERROR");

      if (variant == "success") {
        title = this.$t("COMMON.SUCCESS");
      }

      this.$bvToast.toast(msg, {
        title: title,
        variant: variant,
        toaster: "b-toaster-top-center",
        solid: true,
      });
    },
  },
};
</script>

<style scoped>
.connections-session-cell {
  background: #f8f9fb;
  padding: 16px;
}

.connections-session-table {
  overflow-x: auto;
  width: 100%;
}

.connections-toolbar {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.live-indicator {
  align-items: center;
  color: #1dc9b7;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  margin-right: 16px;
}

.live-indicator__dot {
  background: #1dc9b7;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgba(29, 201, 183, 0.55);
  display: inline-block;
  height: 8px;
  margin-right: 7px;
  width: 8px;
  animation: live-pulse 1.8s infinite;
}

@keyframes live-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(29, 201, 183, 0.55);
  }

  70% {
    box-shadow: 0 0 0 8px rgba(29, 201, 183, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(29, 201, 183, 0);
  }
}
</style>
